// src/pages/AdminProducts.jsx
import React, { useEffect, useMemo, useState, useCallback } from "react";
import {
  Box,
  Heading,
  Spinner,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Text,
  Container,
  Button,
  VStack,
  HStack,
  Badge,
  Image,
  Select,
  useDisclosure,
  IconButton,
  Tooltip,
  Input,
  InputGroup,
  InputLeftElement,
  Kbd,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
} from "@chakra-ui/react";
import { Search2Icon, EditIcon, AddIcon, DeleteIcon, RepeatIcon } from "@chakra-ui/icons";
import { useCognito } from "../utils/useCognito";
import { getProductListClean, upsertProduct, deleteProduct } from "../utils/commonApiCalls";
import ProductEditorModal from "../components/ProductEditorModal";

const AdminProducts = () => {
  const toast = useToast();
  const { getIdToken } = useCognito();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [products, setProducts] = useState([]);
  const [histSelection, setHistSelection] = useState({});
  const [query, setQuery] = useState("");

  // Editor modal state
  const editor = useDisclosure();
  const [editingProduct, setEditingProduct] = useState(null);
  const [form, setForm] = useState({});

  // Delete confirm dialog
  const delDialog = useDisclosure();
  const [deleteTarget, setDeleteTarget] = useState(null);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    try {
      const token = getIdToken();
      const list = (await getProductListClean(token, true)) || [];
      // Accept both {items:[...]} and plain [...]
      const items = Array.isArray(list) ? list : Array.isArray(list?.items) ? list.items : [];
      setProducts(items);

      const initialHist = {};
      items.forEach((p) => {
        const hp = p.historicalPrices || {};
        const dates = Object.keys(hp).sort((a, b) => new Date(b) - new Date(a));
        if (dates.length > 0) initialHist[p.id || p.qCode] = dates[0];
      });
      setHistSelection(initialHist);
      return true;
    } catch (err) {
      console.error("[AdminProducts] getProductListClean failed:", err);
      toast({
        title: "Failed to load products",
        description:
          err?.message === "Failed to fetch"
            ? "Network/CORS issue on product list endpoint."
            : err?.message || String(err),
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [getIdToken, toast]);

  useEffect(() => {
    loadProducts();
  }, [loadProducts]);

  const openAddModal = () => {
    setEditingProduct(null);
    setForm({
      id: "",
      name: "",
      price: 0,
      category: "",
      description: "",
      imageUrl: "",
      isVisible: false,
      partNumbers: {},
      historicalPrices: {},
      coverage: [],
      tags: [],
      qCode: "",
      ptype: "",
      ifuUrl: "",
      mue: 0,
      sizes: [],
    });
    editor.onOpen();
  };

  const openEditModal = (p) => {
    setEditingProduct(p);
    setForm({ ...p });
    editor.onOpen();
  };

  const closeEditor = () => {
    editor.onClose();
    setEditingProduct(null);
  };

  // Accept final payload (already normalized by modal)
  const onSave = async (payload) => {
    setSaving(true);
    try {
      const token = getIdToken();

      // small guard
      if (payload?.historicalPrices && typeof payload.historicalPrices === "object") {
        payload.historicalPrices = Object.fromEntries(
          Object.entries(payload.historicalPrices).map(([k, v]) => [k, Number(v)])
        );
      }

      await upsertProduct(payload, token);

      toast({ title: "Product saved", status: "success", duration: 2500, isClosable: true });
      closeEditor();

      // Refresh in background; warn (don’t error) if it fails
      const ok = await loadProducts();
      if (!ok) {
        toast({
          title: "List reload failed",
          description: "The product was saved, but the table didn’t refresh. Click Reload.",
          status: "warning",
          duration: 6000,
          isClosable: true,
        });
      }
    } catch (err) {
      console.error("[AdminProducts] upsert failed:", err);
      toast({
        title: "Save failed",
        description:
          err?.message === "Failed to fetch"
            ? "Network/CORS issue when saving."
            : err?.message || String(err),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      setSaving(false);
    }
  };

  const requestDelete = (p) => {
    setDeleteTarget(p);
    delDialog.onOpen();
  };

  const onConfirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      const token = getIdToken();
      await deleteProduct(deleteTarget.id || deleteTarget.qCode, token);
      toast({ title: "Product deleted", status: "success", duration: 2500, isClosable: true });
      await loadProducts();
    } catch (err) {
      console.error("[AdminProducts] delete failed:", err);
      toast({
        title: "Delete failed",
        description:
          err?.message === "Failed to fetch"
            ? "Network/CORS issue when deleting."
            : err?.message || String(err),
        status: "error",
        duration: 6000,
        isClosable: true,
      });
    } finally {
      delDialog.onClose();
      setDeleteTarget(null);
    }
  };

  const filtered = useMemo(() => {
    const q = (query || "").trim().toLowerCase();
    if (!q) return products;
    return products.filter((p) => {
      const hay =
        `${p.name || ""} ${p.description || ""} ${p.category || ""} ${p.id || ""} ${p.qCode || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [products, query]);

  if (loading) {
    return (
      <Container maxW="7xl" py={10}>
        <VStack spacing={4}>
          <Heading size="lg">Admin Products</Heading>
          <Spinner />
        </VStack>
      </Container>
    );
  }

  return (
    <Container maxW="7xl" py={8}>
      <VStack align="stretch" spacing={6}>
        <HStack justify="space-between" flexWrap="wrap" gap={3}>
          <HStack>
            <Heading size="lg">Admin Products</Heading>
            <Tooltip label="Reload">
              <IconButton
                aria-label="Reload"
                icon={<RepeatIcon />}
                size="sm"
                variant="ghost"
                onClick={() =>
                  loadProducts().then((ok) => {
                    if (!ok) {
                      toast({
                        title: "Reload failed",
                        description: "Try again or check your connection / API CORS.",
                        status: "warning",
                        duration: 5000,
                        isClosable: true,
                      });
                    }
                  })
                }
              />
            </Tooltip>
          </HStack>

          <HStack>
            <InputGroup size="sm" w={{ base: "full", sm: "280px" }}>
              <InputLeftElement pointerEvents="none">
                <Search2Icon />
              </InputLeftElement>
              <Input
                placeholder="Search name, category, ID…"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </InputGroup>

            <Button leftIcon={<AddIcon />} colorScheme="teal" size="sm" onClick={openAddModal}>
              Add Product
            </Button>
          </HStack>
        </HStack>

        <Box
          bg="white"
          _dark={{ bg: "gray.800" }}
          borderRadius="2xl"
          shadow="sm"
          borderWidth="1px"
          overflow="hidden"
        >
          <Box overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg="gray.50" _dark={{ bg: "gray.700" }} position="sticky" top={0} zIndex={1}>
                <Tr>
                  <Th>Product</Th>
                  <Th>Category</Th>
                  <Th isNumeric>Price</Th>
                  <Th>Visible</Th>
                  <Th>Part Numbers</Th>
                  <Th>Historical Price</Th>
                  <Th>Actions</Th>
                </Tr>
              </Thead>
              <Tbody>
                {filtered.map((p) => {
                  const id = p.id || p.qCode;
                  const partNumbers = p.partNumbers || {};
                  const hist = p.historicalPrices || {};
                  const histDates = Object.keys(hist).sort((a, b) => new Date(b) - new Date(a));
                  const selectedDate = histSelection[id];

                  return (
                    <Tr key={id}>
                      <Td>
                        <HStack align="start" spacing={3}>
                          <Image
                            boxSize="56px"
                            objectFit="cover"
                            src={
                              p.imageUrl
                                ? `https://www.curagenesis.com/assets/img/graft/${p.imageUrl}`
                                : "https://via.placeholder.com/56x56.png?text=—"
                            }
                            alt={p.name}
                            borderRadius="md"
                            borderWidth="1px"
                          />
                          <Box>
                            <Text fontWeight="semibold">{p.name}</Text>
                            <Text fontSize="xs" color="gray.500" noOfLines={2}>
                              {p.description || "—"}
                            </Text>
                            <Text fontSize="xs" color="gray.400">
                              <Kbd>{id}</Kbd>
                            </Text>
                          </Box>
                        </HStack>
                      </Td>

                      <Td>{p.category || "—"}</Td>

                      <Td isNumeric>${Number(p.price || 0).toFixed(2)}</Td>

                      <Td>
                        <Badge colorScheme={p.isVisible ? "green" : "red"}>{p.isVisible ? "Yes" : "No"}</Badge>
                      </Td>

                      <Td>
                        {Object.keys(partNumbers).length ? (
                          <Box bg="blue.50" _dark={{ bg: "blue.900" }} p={2} borderRadius="md">
                            {Object.entries(partNumbers).map(([size, pn]) => (
                              <HStack key={size} spacing={3}>
                                <Badge colorScheme="blue" variant="subtle" fontSize="xs">
                                  {size}
                                </Badge>
                                <Text fontSize="xs">{pn}</Text>
                              </HStack>
                            ))}
                          </Box>
                        ) : (
                          <Text fontSize="xs">—</Text>
                        )}
                      </Td>

                      <Td>
                        {histDates.length > 0 ? (
                          <Select
                            size="sm"
                            value={selectedDate || histDates[0]}
                            onChange={(e) => setHistSelection((s) => ({ ...s, [id]: e.target.value }))}
                          >
                            {histDates.map((d) => (
                              <option key={d} value={d}>
                                {d} — ${Number(hist[d]).toFixed(2)}
                              </option>
                            ))}
                          </Select>
                        ) : (
                          <Text fontSize="xs">—</Text>
                        )}
                      </Td>

                      <Td>
                        <HStack>
                          <Tooltip label="Edit">
                            <IconButton
                              aria-label="Edit"
                              icon={<EditIcon />}
                              size="sm"
                              onClick={() => openEditModal(p)}
                            />
                          </Tooltip>
                          <Tooltip label="Delete">
                            <IconButton
                              aria-label="Delete"
                              icon={<DeleteIcon />}
                              size="sm"
                              colorScheme="red"
                              variant="outline"
                              onClick={() => requestDelete(p)}
                            />
                          </Tooltip>
                        </HStack>
                      </Td>
                    </Tr>
                  );
                })}
              </Tbody>
            </Table>
          </Box>
        </Box>
      </VStack>

      {/* Editor Modal */}
      <ProductEditorModal
        isOpen={editor.isOpen}
        onClose={closeEditor}
        form={form}
        setForm={setForm}
        isEditing={!!editingProduct}
        isSaving={saving}
        onSave={onSave}
        onRequestDelete={() => {
          if (editingProduct) {
            requestDelete(editingProduct);
          }
        }}
      />

      {/* Delete Confirmation */}
      <AlertDialog isOpen={delDialog.isOpen} leastDestructiveRef={undefined} onClose={delDialog.onClose}>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Product
            </AlertDialogHeader>

            <AlertDialogBody>
              Are you sure you want to delete{" "}
              <strong>{deleteTarget?.name || deleteTarget?.id || deleteTarget?.qCode}</strong>? This cannot be undone.
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button onClick={delDialog.onClose} variant="ghost" mr={3}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={onConfirmDelete} leftIcon={<DeleteIcon />}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Container>
  );
};

export default AdminProducts;
